import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import api, { formatApiError, setAuthToken } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: password });
      if (data?.access_token) setAuthToken(data.access_token);
      await refresh?.();
      toast.success("Password updated — you're logged in");
      navigate("/map");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not reset password");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-[calc(100vh-4rem)] grid place-items-center px-4 py-10" data-testid="reset-password-invalid">
        <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 text-center space-y-3">
          <h1 className="font-display font-bold text-2xl">Invalid reset link</h1>
          <p className="text-sm text-muted-foreground">
            This link is missing the reset token. Request a new one from the login page.
          </p>
          <Link to="/login" className="inline-block text-sm text-primary hover:underline">Back to login</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] grid place-items-center px-4 py-10" data-testid="reset-password-page">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-md rounded-2xl border border-border bg-card p-6 sm:p-8 space-y-5"
      >
        <div>
          <h1 className="font-display font-bold text-3xl tracking-tight">Choose a new password</h1>
          <p className="text-sm text-muted-foreground mt-1">
            At least 8 characters. You'll stay signed in after you save.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="new-password">New password</Label>
          <Input
            id="new-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
            autoFocus
            data-testid="reset-new-password-input"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirm-password">Confirm password</Label>
          <Input
            id="confirm-password"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
            data-testid="reset-confirm-password-input"
          />
        </div>

        <Button
          type="submit"
          disabled={loading}
          className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-11 rounded-full"
          data-testid="reset-submit-btn"
        >
          {loading ? "Saving…" : "Save new password"}
        </Button>
      </form>
    </div>
  );
}
